<template>
	<b-card no-body class="analysis-chart-card">
		<div v-if="loading" class="analysis-chart-state">
			<b-spinner variant="primary" />
		</div>

		<div v-else-if="!hasData" class="analysis-chart-state">
			<feather-icon icon="BarChart2Icon" size="42" class="text-muted mb-1" />
			<h5 class="mb-25">No data yet</h5>
			<p class="text-muted mb-0">
				{{ emptyMessage }}
			</p>
		</div>

		<vue-apex-charts
			v-else
			type="area"
			height="325px"
			:options="chartOptions"
			:series="series"
		/>
	</b-card>
</template>

<script>
import VueApexCharts from "vue-apexcharts";
import axiosIns from "@/libs/axios";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import flockAnalysisUpdate from "@/libs/flockAnalysisUpdate";

export default {
	components: {
		VueApexCharts,
	},
	props: {
		endpoint: {
			type: String,
			required: true,
		},
		dataKey: {
			type: String,
			required: true,
		},
		standardKey: {
			type: String,
			required: true,
		},
		seriesLabel: {
			type: String,
			required: true,
		},
		standardLabel: {
			type: String,
			required: true,
		},
		yaxisTitle: {
			type: String,
			default: "Value",
		},
		emptyMessage: {
			type: String,
			default: "Select a flock with recorded entries to see this chart.",
		},
	},
	data() {
		return {
			loading: true,
			hasData: false,
			series: [
				{ name: this.seriesLabel, data: [] },
				{ name: this.standardLabel, data: [] },
			],
			chartOptions: {
				chart: {
					height: 350,
					type: "area",
					toolbar: { show: false },
					fontFamily: "inherit",
					animations: {
						enabled: true,
						easing: "easeinout",
						speed: 600,
						animateGradually: {
							enabled: true,
							delay: 150,
						},
						dynamicAnimation: {
							enabled: true,
							speed: 350,
						},
					},
				},
				colors: ["#7367f0", "#28c76f"],
				dataLabels: { enabled: false },
				stroke: {
					curve: "smooth",
					width: [3, 2],
					dashArray: [0, 6],
				},
				fill: {
					type: "gradient",
					gradient: {
						shadeIntensity: 1,
						opacityFrom: 0.35,
						opacityTo: 0.05,
						stops: [0, 90, 100],
					},
				},
				title: {
					text: "Graph Analysis & Current Report",
					align: "left",
					style: { fontSize: "14px", fontWeight: 600 },
				},
				grid: {
					borderColor: "#ebe9f1",
					strokeDashArray: 4,
					padding: { top: -10 },
				},
				markers: {
					size: 4,
					strokeWidth: 2,
					hover: { size: 6 },
				},
				tooltip: {
					shared: true,
					intersect: false,
				},
				xaxis: {
					categories: [],
					title: { text: "Weeks" },
					labels: {
						rotate: 0,
						hideOverlappingLabels: true,
						style: { fontSize: "11px" },
					},
					tickAmount: 6,
				},
				yaxis: {
					min: 0,
					title: { text: this.yaxisTitle },
				},
				legend: {
					position: "top",
					horizontalAlign: "right",
					floating: true,
					offsetY: -25,
					offsetX: -5,
				},
			},
		};
	},
	created() {
		this.getData(this.$route.params.flockId);
	},
	mounted() {
		flockAnalysisUpdate.$on("flockIdUpdateEvent", this.getData);
	},
	beforeDestroy() {
		flockAnalysisUpdate.$off("flockIdUpdateEvent", this.getData);
	},
	methods: {
		getData(flockId) {
			this.loading = true;
			const url = flockId != null ? `${this.endpoint}?flockId=${flockId}` : this.endpoint;

			axiosIns
				.get(url)
				.then((response) => {
					const rows = Array.isArray(response.data) ? response.data : [];

					this.hasData = rows.length > 0;
					this.series = [
						{ name: this.seriesLabel, data: rows.map((row) => this.toNumber(row[this.dataKey])) },
						{ name: this.standardLabel, data: rows.map((row) => this.toNumber(row[this.standardKey])) },
					];
					this.chartOptions = {
						...this.chartOptions,
						xaxis: {
							...this.chartOptions.xaxis,
							categories: rows.map((row) => this.formatWeekLabel(row.interval)),
						},
					};
				})
				.catch((error) => {
					this.hasData = false;
					this.$toast({
						component: ToastificationContent,
						position: "top-right",
						props: {
							icon: "AlertTriangleIcon",
							variant: "danger",
							text: error.response?.data?.message || "Unable to load chart data.",
						},
					});
				})
				.finally(() => {
					this.loading = false;
				});
		},
		toNumber(value) {
			const num = parseFloat(value);
			return Number.isFinite(num) ? Math.round(num * 100) / 100 : 0;
		},
		// The API sends each point's full window as "YYYY-MM-DD to YYYY-MM-DD"
		// — fine as data, but as an x-axis tick label on 6-9+ points it forced
		// steep rotation and ran the axis into the plot. A single short date
		// (the week's start) is enough to read the trend at a glance; the full
		// range is still available in the tooltip via the row data itself.
		formatWeekLabel(interval) {
			const startDate = String(interval).split(" to ")[0];
			const parsed = new Date(startDate);
			if (Number.isNaN(parsed.getTime())) {
				return startDate;
			}
			return parsed.toLocaleDateString("en-US", { month: "short", day: "numeric" });
		},
	},
};
</script>

<style lang="scss" scoped>
.analysis-chart-state {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	min-height: 325px;
	text-align: center;
	padding: 2rem;
}
</style>
